import React, { useState, useEffect } from 'react';
import {
  CButton, CCard, CCardHeader, CCardBody, CCol, CContainer, CRow
} from '@coreui/react';
import { toast } from 'react-toastify';
import { getDatabase, ref, get, query, orderByChild, equalTo, set, update } from 'firebase/database';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BusinessDetailsForm from './CreatorComponents/BusinessDetailsForm';
import { EmbeddedComponentWrapper } from './CreatorComponents/EmbeddedComponentWrapper';
import Onboarding from './CreatorComponents/Onboarding';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../../utils/EnvironmentSwitcher';

const StripeBuyButton = ({ userId, userEmail }) => {
  useEffect(() => {
    const loadStripeBuyButtonScript = () => new Promise((resolve, reject) => {
      // If script already present, resolve immediately
      if (document.getElementById('stripe-buy-button-js')) return resolve();

      const script = document.createElement('script');
      script.id = 'stripe-buy-button-js';
      script.src = 'https://js.stripe.com/v3/buy-button.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Stripe Buy Button script'));
      document.head.appendChild(script);
    });

    const renderButton = async () => {
      try {
        await loadStripeBuyButtonScript();

        const container = document.getElementById('stripe-button-container');
        if (!container) return;

        // Clear any previous instance to avoid duplicates on re-render
        container.innerHTML = '';

        const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        const buyButtonId = import.meta.env.VITE_STRIPE_BUY_BUTTON_ID;

        if (!publishableKey || !buyButtonId) {
          console.error('Stripe Buy Button: Missing REACT_APP_STRIPE_PUBLISHABLE_KEY or REACT_APP_STRIPE_BUY_BUTTON_ID');
          return;
        }

        const button = document.createElement('stripe-buy-button');
        button.setAttribute('buy-button-id', buyButtonId);
        button.setAttribute('publishable-key', publishableKey);

        // Optional context for tracking/checkout prefill
        if (userId) button.setAttribute('client-reference-id', userId);
        if (userEmail) button.setAttribute('customer-email', userEmail);

        container.appendChild(button);
      } catch (err) {
        console.error('Stripe Buy Button initialization error:', err);
      }
    };

    renderButton();
  }, [userId, userEmail]);

  return <div id="stripe-button-container"></div>;
};

const CreatorPage = ({ authUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [loading, setLoading] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [hasPaid, setHasPaid] = useState(false);
  const [accountDetails, setAccountDetails] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check onboarding status and redirect if completed
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Don't check if we're already on the dashboard
      if (location.pathname === '/dashboard/creator/home') {
        console.log('Already on dashboard, skipping redirect check');
        return;
      }

      if (!authUser?.authUser?.email) {
        console.log('No auth user email, skipping redirect check');
        return;
      }

      try {
        console.log('Checking onboarding status for:', authUser.authUser.email);
        
        const db = getDatabase();
        const purchaseRef = ref(db, 'verified_creators');
        const emailQuery = query(
          purchaseRef,
          orderByChild('email'),
          equalTo(authUser.authUser.email)
        );

        const snapshot = await get(emailQuery);
        if (snapshot.exists()) {
          const entries = Object.values(snapshot.val());
          const mostRecent = entries.reduce((latest, current) =>
            (current.updatedAt > latest.updatedAt) ? current : latest
          );

          if (mostRecent.onboardingStatus === 'complete') {
            console.log('Onboarding complete, navigating to dashboard');
            navigate('/dashboard/creator/home', { 
              state: { activeLink: 'creator' },
              replace: true 
            });
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };

    checkOnboardingStatus();
  }, [authUser, navigate, location.pathname]);

  // Handle return from Stripe
  useEffect(() => {
    const handleStripeReturn = async () => {
      // Check if we're returning from Stripe (either via URL param or full URL)
      const url = window.location.href;
      const returnMatch = url.match(/return\/(acct_[a-zA-Z0-9]+)/);
      const stripeAccountId = accountId || (returnMatch ? returnMatch[1] : null);
      
      if (!stripeAccountId || !authUser?.authUser) return;

      try {
        console.log('Verifying creator after Stripe return:', stripeAccountId);
        
        const response = await fetch(`${config.paymentUrl}/verifyCreator`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.authUser.accessToken}`,
          },
          body: JSON.stringify({
            email: authUser.authUser.email,
            uid: authUser.authUser.uid
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Verification response:', data);
        
        if (data.verified) {
          // After successful verification, update onboarding status
          const db = getDatabase();
          const creatorsRef = ref(db, 'verified_creators');
          const emailQuery = query(
            creatorsRef,
            orderByChild('email'),
            equalTo(authUser.authUser.email)
          );

          const snapshot = await get(emailQuery);
          if (snapshot.exists()) {
            const creatorKey = Object.keys(snapshot.val())[0];
            await update(ref(db, `verified_creators/${creatorKey}`), {
              onboardingStatus: 'completed',
              updatedAt: new Date().toISOString()
            });
          }

          toast.success('Stripe account setup completed!');
          navigate('/dashboard/creator/home', { 
            state: { activeLink: 'creator' },
            replace: true 
          });
        } else {
          toast.error('Account verification failed');
          navigate('/dashboard', { 
            state: { activeLink: 'creator' },
            replace: true 
          });
        }
      } catch (error) {
        console.error('Error checking account status:', error);
        toast.error('Failed to verify account status');
        navigate('/dashboard', { 
          state: { activeLink: 'creator' },
          replace: true 
        });
      }
    };

    handleStripeReturn();
  }, [authUser, navigate, accountId]);

  // Update useEffect to set hasPaid based on accountDetails
  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (!authUser?.authUser?.email) return;

      try {
        const db = getDatabase();
        const purchaseRef = ref(db, 'purchase_history');
        const emailQuery = query(
          purchaseRef,
          orderByChild('email'),
          equalTo(authUser.authUser.email)
        );

        const snapshot = await get(emailQuery);
        if (snapshot.exists()) {
          const entries = Object.values(snapshot.val());
          const mostRecent = entries.reduce((latest, current) =>
            (current.timestamp > latest.timestamp) ? current : latest
          );

          // Check for lifetime subscription
          const hasLifetimeSubscription = mostRecent.line_items?.some(item => 
            item.price === 5000 && 
            item.product_name?.toLowerCase() === "mieros lifetime subscription".toLowerCase()
          );

          setAccountDetails(mostRecent);
          setHasPaid(mostRecent.payment_status === 'paid' && hasLifetimeSubscription);
          if (mostRecent.stripeAccountId) {
            setStripeAccountId(mostRecent.stripeAccountId);
          }
        }
      } catch (error) {
        console.error('Error fetching account details:', error);
      }
    };

    fetchAccountDetails();
  }, [authUser]);

  // Show loading state if authUser isn't ready
  if (!authUser?.authUser) {
    return (
      <div className="app flex-row align-items-center">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol md="6">
              <CCard className="p-4">
                <CCardBody className="text-center">
                  <div>Loading...</div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    );
  }

  const handlePaymentClick = () => {
    window.open('https://buy.stripe.com/test_9AQdQQdluaXYe3KfZ1', '_blank');
    toast.info('After successful payment, return here and click "Verify Payment"');
  };

  const verifyPayment = async () => {
    if (!authUser.authUser?.email) {
      toast.error('Please login first');
      return;
    }

    setLoading(true);
    try {
      console.log('Verifying payment for:', authUser.authUser.email);

      const response = await fetch(`${config.paymentUrl}/verifyCreator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${authUser.authUser.accessToken}`,
        },
        body: JSON.stringify({
          email: authUser.authUser.email,
          uid: authUser.authUser.uid
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Verification response:', data);
      
      if (data.verified) {
        setHasPaid(true);
        toast.success('Payment verified! You can now create your Stripe account.');
        console.log('Payment ID:', data.paymentId);
        console.log('Payment timestamp:', data.timestamp);
      } else {
        toast.error('Payment not found. Please try again in a few moments.');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error(`Failed to verify payment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const BASE_URL = import.meta.env.VITE_BASE_URL || 'https://localhost:3000';

  const handleBusinessDetailsSubmit = async (formData) => {
    try {
      const accountResponse = await fetch(`${config.paymentUrl}/account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.authUser.accessToken}`,
        },
        body: JSON.stringify({
          ...formData,
          email: authUser.authUser.email,
          uid: authUser.authUser.uid,
        })
      });

      if (!accountResponse.ok) {
        throw new Error(`HTTP error! status: ${accountResponse.status}`);
      }

      const { account } = await accountResponse.json();
      
      // Update Firebase with initial onboarding status
      const db = getDatabase();
      const creatorsRef = ref(db, 'verified_creators');
      const emailQuery = query(
        creatorsRef,
        orderByChild('email'),
        equalTo(authUser.authUser.email)
      );

      const snapshot = await get(emailQuery);
      if (snapshot.exists()) {
        const creatorKey = Object.keys(snapshot.val())[0];
        await update(ref(db, `verified_creators/${creatorKey}`), {
          stripeAccountId: account,
          onboardingStatus: 'initiated',
          updatedAt: new Date().toISOString()
        });
      }

      setStripeAccountId(account);
      setShowOnboarding(true);

    } catch (error) {
      console.error('Error creating Stripe account:', error);
      toast.error(`Failed to create Stripe account: ${error.message}`);
    }
  };

  return (
    <div className="app flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md="6">
            <CCard className="p-4">
              <CCardHeader>
                <h1>Create Creator Account</h1>
              </CCardHeader>
              <CCardBody>
                <p className="mb-2">
                  {hasPaid 
                    ? "Thank you for your purchase! You can now continue to create your creator account and connect with Stripe."
                    : "Create your creator account and connect with Stripe. Only one account per user."}
                </p>

                {!hasPaid ? (
                  <div>
                    <StripeBuyButton
                      userId={authUser.authUser.uid}
                      userEmail={authUser.authUser.email}
                    />
                    <p className="mb-2">After successful payment, return here and click "Verify Payment"</p>
                    <div className="mt-3">
                      <CButton
                        color="primary"
                        className="px-4"
                        onClick={verifyPayment}
                        disabled={loading}
                      >
                        {loading ? 'Verifying...' : 'Verify Payment'}
                      </CButton>
                    </div>
                  </div>
                ) : showOnboarding ? (
                  <EmbeddedComponentWrapper accountId={stripeAccountId}>
                    <Onboarding 
                      authUser={authUser} 
                      stripeAccountId={stripeAccountId}
                    />
                  </EmbeddedComponentWrapper>
                ) : (
                  <BusinessDetailsForm
                    authUser={authUser}
                    onSubmit={handleBusinessDetailsSubmit}
                  />
                )}

                {stripeAccountId && (
                  <div className="mt-3">
                    <p>Stripe Account ID: {stripeAccountId}</p>
                  </div>
                )}
              </CCardBody>
            </CCard>

            {accountDetails && (
              <CCard className="p-4">
                <CCardHeader>
                  <h2>Account Details</h2>
                </CCardHeader>
                <CCardBody>
                  <div className="mb-3">
                    <strong>Email:</strong> {accountDetails.email}
                  </div>
                  {accountDetails.stripeAccountId && (
                    <div className="mb-3">
                      <strong>Stripe Account ID:</strong> {accountDetails.stripeAccountId}
                    </div>
                  )}
                  {accountDetails.onboardingStatus && (
                    <div className="mb-3">
                      <strong>Onboarding Status:</strong>{' '}
                      <span className={`badge bg-${accountDetails.onboardingStatus === 'complete' ? 'success' : 'warning'}`}>
                        {accountDetails.onboardingStatus}
                      </span>
                    </div>
                  )}
                  {accountDetails.accountLink && (
                    <div className="mb-3">
                      <strong>Account Link:</strong>{' '}
                      <CButton
                        color="link"
                        href={accountDetails.accountLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!accountDetails.accountLink}
                      >
                        Complete Onboarding
                      </CButton>
                    </div>
                  )}
                  <div className="mb-3">
                    <strong>Created:</strong>{' '}
                    {new Date(accountDetails.created).toLocaleDateString()}
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong>{' '}
                    <span className={`badge bg-${accountDetails.status === 'paid' ? 'success' : 'secondary'}`}>
                      {accountDetails.status}
                    </span>
                  </div>
                  {accountDetails.paymentId && (
                    <div className="mb-3">
                      <strong>Payment ID:</strong> {accountDetails.paymentId}
                    </div>
                  )}
                </CCardBody>
              </CCard>
            )}
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default CreatorPage;


